import React, { useMemo, useState } from 'react'
import { Stack } from '@mui/material'
import ScrollTable from 'components/global/CUK/ScrollTable'
import { useParams } from 'react-router-dom'

import { useNavigate } from 'router'
import TableView, { useTable } from 'packages/ui/Table'
import TableSkeleton from 'components/global/CUK/Skeletons/TableSkeleton'

import { getColumnsByHardwareType } from './config-columns'
import useSort from '../use-sort'
import { HARDWARE_VIEW_MODES } from './use-hardware-view-mode'

const PAGE_SIZE = 20

const useSelection = ({ equipments, apiSort }) => {
  const [page, setPage] = useState(0)

  const selection = useMemo(() => {
    return apiSort.sort(equipments).slice(0, (page + 1) * PAGE_SIZE)
  }, [equipments, page, apiSort.orderBy, apiSort.orderDirection])

  return {
    selection,
    count: equipments.length,
    fetchNextPage: () => setPage(prev => prev + 1),
    shouldFetchNextPage: (page + 1) * PAGE_SIZE < equipments.length
  }
}

const Table = ({ equipments, hardwareType, loading, viewMode, basePath, buildSearch, detailsEnabled }) => {
  const { uuid } = useParams()
  const sort = useSort({ defaultOrderBy: 'available_level' })
  const { selection, fetchNextPage, shouldFetchNextPage } = useSelection({ equipments, apiSort: sort })
  const navigate = useNavigate()
  const isDetailedView = viewMode === HARDWARE_VIEW_MODES.COMPLETE
  const columns = useMemo(() => getColumnsByHardwareType(hardwareType, {
    isDetailedView,
    cardOpen: Boolean(detailsEnabled && uuid)
  }), [detailsEnabled, hardwareType, isDetailedView, uuid])

  const apiTable = useTable({
    items: selection,
    columns,
    onRowClick: detailsEnabled
      ? (equipment) => navigate({
          pathname: basePath ? `${basePath}/${equipment.uuid}` : `${equipment.uuid}`,
          search: buildSearch ? buildSearch({ view: viewMode || HARDWARE_VIEW_MODES.SIMPLE }) : ''
        })
      : undefined,
  })

  return (
    <Stack
      sx={{
        overflow: 'hidden',
        overflowY: 'auto',
        flex: '1 1 auto',
        minWidth: 0,
        width: '100%',
        maxWidth: '100%',
        scrollbarWidth: 'none',
        height: '100%',
        '&::-webkit-scrollbar ': {
          display: 'none',
        },
      }}
    >
      <ScrollTable
        fetchNextPage={fetchNextPage}
        shouldFetchNextPage={shouldFetchNextPage}
        collapse={Boolean(detailsEnabled && uuid)}
        style={{
          maxWidth: '100%',
          overflowX: isDetailedView ? 'auto' : 'hidden',
          ...(isDetailedView && {
            '& .MuiTableCell-head': { lineHeight: '16px' }
          })
        }}
      >
        {loading ? (
          <TableSkeleton />
        ) : (
          <TableView
            apiTable={apiTable}
            sort={{
              by: sort.orderBy,
              direction: sort.orderDirection,
              onChange: (name) => {
                const column = columns.find((column) => column.name === name)

                sort.orderByChange(column.sortBy || column.name)
              },
            }}
          />
        )}
      </ScrollTable>
    </Stack>
  )
}

export default Table
